import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Printer, Check, Star, Save, RotateCcw } from "lucide-react";
import {
  TACC_PRODUCTS, GUMROAD_PRODUCTS, GUMROAD_URL, AMAZON_BOOKS, MODULES,
  B2B_TIERS, ENTERPRISE_SEAT_TIERS, DEFAULT_REFERENCE_PRICES, mergeReferencePrices,
  enterpriseQuote, openPricingSheet, money, tiersFromApi, tiersToApi,
} from "@/lib/pricingCatalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ tag, title, children }) => (
  <div className="mb-12">
    <div className="section-tag mb-2">§ {tag}</div>
    <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-5">{title}</h3>
    {children}
  </div>
);

const seatLabel = (t) => (t.max === Infinity ? `${t.min.toLocaleString()}+` : `${t.min.toLocaleString()}–${t.max.toLocaleString()}`);

const numGroup = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, Number(v) || 0]));
const numPrices = (p) => JSON.stringify({ tacc: numGroup(p.tacc), gumroad: numGroup(p.gumroad), b2b: numGroup(p.b2b) });
const numTiers = (t) => JSON.stringify(t.map((x) => ({ min: x.min, max: x.max === Infinity ? null : x.max, r: Number(x.perSeatYear) || 0 })));

const PriceInput = ({ value, onChange, testId, big }) => (
  <span className="inline-flex items-center gap-1">
    <span className={big ? "text-[#B45309] text-2xl" : "text-[#A8A29E] text-sm"}>$</span>
    <input
      type="number" min="0" value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={`bg-[#FAF7F2] border border-[#E7DFD5] rounded px-2 py-1 text-right focus:outline-none focus:border-[#F59E0B] ${big ? "w-28 text-3xl font-display text-[#B45309]" : "w-20 text-sm text-[#1C1917]"}`}
    />
  </span>
);

export const AdminPricingTab = () => {
  const [headcount, setHeadcount] = useState(2500);
  const [tiers, setTiers] = useState(ENTERPRISE_SEAT_TIERS);
  const [savedTiers, setSavedTiers] = useState(ENTERPRISE_SEAT_TIERS);
  const [prices, setPrices] = useState(DEFAULT_REFERENCE_PRICES);
  const [savedPrices, setSavedPrices] = useState(DEFAULT_REFERENCE_PRICES);
  const [saving, setSaving] = useState(false);

  const quote = enterpriseQuote(headcount, tiers);
  const dirtyPrices = numPrices(prices) !== numPrices(savedPrices);
  const dirtyTiers = numTiers(tiers) !== numTiers(savedTiers);
  const dirty = dirtyPrices || dirtyTiers;

  useEffect(() => {
    axios.get(`${API}/admin/pricing/enterprise`)
      .then((r) => { const t = tiersFromApi(r.data.tiers); if (t.length) { setTiers(t); setSavedTiers(t); } })
      .catch(() => {});
    axios.get(`${API}/admin/pricing/products`)
      .then((r) => { const p = mergeReferencePrices(r.data.prices); setPrices(p); setSavedPrices(p); })
      .catch(() => {});
  }, []);

  const setPrice = (group, key, value) =>
    setPrices((prev) => ({ ...prev, [group]: { ...prev[group], [key]: value } }));
  const setRate = (idx, value) =>
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, perSeatYear: value } : t)));

  const undo = () => { setPrices(savedPrices); setTiers(savedTiers); };

  const saveAll = async () => {
    setSaving(true);
    try {
      if (dirtyPrices) {
        const r = await axios.put(`${API}/admin/pricing/products`, {
          tacc: numGroup(prices.tacc), gumroad: numGroup(prices.gumroad), b2b: numGroup(prices.b2b),
        });
        const p = mergeReferencePrices(r.data.prices); setPrices(p); setSavedPrices(p);
      }
      if (dirtyTiers) {
        const r = await axios.put(`${API}/admin/pricing/enterprise`, { tiers: tiersToApi(tiers) });
        const t = tiersFromApi(r.data.tiers); setTiers(t); setSavedTiers(t);
      }
      toast.success("Prices saved.", { description: "Your reference numbers, one-pager and lead proposals all use these now." });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save your prices.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-pricing-tab">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-10">
        <p className="text-[#78716C] text-sm max-w-xl leading-relaxed">
          Your complete price list — every product, what it is, and what it unlocks. Edit any number and hit <span className="text-[#1C1917]">Save</span>. Keep this open when you talk to companies so you always know your numbers.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {dirty && (
            <button onClick={undo} className="btn-outline text-xs" data-testid="pricing-undo-btn"><RotateCcw size={13} /> Undo</button>
          )}
          <button onClick={saveAll} disabled={saving || !dirty} className="btn-ember text-sm disabled:opacity-40" data-testid="pricing-save-btn">
            <Save size={15} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
          <button onClick={() => openPricingSheet(headcount, tiers, prices)} data-testid="pricing-print-btn" className="btn-outline text-sm">
            <Printer size={15} /> Print
          </button>
        </div>
      </div>

      {/* ---------- On TACC.love ---------- */}
      <Section tag="On TACC.love · your site" title="Bought directly on your site (Stripe)">
        <div className="grid gap-4 md:grid-cols-3">
          {TACC_PRODUCTS.map((p) => (
            <div key={p.key} data-testid={`pricing-tacc-${p.key}`} className={`tacc-card p-6 relative ${p.best ? "ring-1 ring-[#F59E0B]/40" : ""}`}>
              {p.best && <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[#B45309] text-[10px] font-mono uppercase tracking-[0.2em]"><Star size={11} /> Best value</div>}
              <div className="font-display text-xl mb-2">{p.name}</div>
              <div className="mb-3"><PriceInput value={prices.tacc[p.key]} onChange={(v) => setPrice("tacc", p.key, v)} testId={`price-tacc-${p.key}`} big /></div>
              <p className="text-sm text-[#44403C] mb-4 leading-relaxed">{p.desc}</p>
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#78716C]">Unlocks</div>
              <div className="text-sm text-[#1C1917] mt-1">{p.unlocks}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#A8A29E] mt-3">These are your <span className="text-[#78716C]">reference</span> numbers. What a customer is actually charged at checkout is set in Stripe — tell me the product + price if you want to change the live charge.</p>
      </Section>

      {/* ---------- Gumroad ---------- */}
      <Section tag="On Gumroad · off-site" title="The versions you link out to">
        <div className="tacc-card overflow-x-auto">
          <table className="w-full text-sm min-w-[420px]">
            <thead>
              <tr className="text-left font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] border-b border-[#E7DFD5]">
                <th className="p-4">Product</th><th className="p-4">Price</th><th className="p-4">Where</th>
              </tr>
            </thead>
            <tbody>
              {GUMROAD_PRODUCTS.map((p) => (
                <tr key={p.key} className="border-b border-[#E7DFD5]">
                  <td className="p-4 text-[#1C1917]">{p.name}</td>
                  <td className="p-4"><PriceInput value={prices.gumroad[p.key]} onChange={(v) => setPrice("gumroad", p.key, v)} testId={`price-gumroad-${p.key}`} /></td>
                  <td className="p-4"><a href={GUMROAD_URL} target="_blank" rel="noreferrer" className="text-[#78716C] underline decoration-[#F59E0B]/40 underline-offset-4 hover:text-[#B45309]">Gumroad ↗</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#A8A29E] mt-2">Priced a little higher than your site — the extra covers Gumroad's cut. Same content. (Set the live Gumroad price on Gumroad; this is your reference.)</p>
      </Section>

      {/* ---------- Amazon books ---------- */}
      <Section tag="Books · Amazon" title="On Amazon (Amazon sets the price)">
        <div className="grid gap-4 md:grid-cols-2">
          {AMAZON_BOOKS.map((b) => (
            <div key={b.name} className="tacc-card p-6">
              <div className="font-display text-lg mb-1">{b.name}</div>
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#78716C] mb-3">{b.format} · Amazon-priced</div>
              <p className="text-sm text-[#44403C] mb-4 leading-relaxed">{b.desc}</p>
              <a href={b.url} target="_blank" rel="noreferrer" className="text-sm text-[#B45309] underline underline-offset-4">View on Amazon ↗</a>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- The 8 modules ---------- */}
      <Section tag="What's inside" title="The 8 healing modules">
        <div className="tacc-card overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <tbody>
              {MODULES.map((m) => (
                <tr key={m.number} className="border-b border-[#E7DFD5] last:border-0">
                  <td className="p-3 font-mono text-[#B45309] w-12">{m.number}</td>
                  <td className="p-3 text-[#1C1917]">{m.title}</td>
                  <td className="p-3 text-[#78716C]">{m.focus}</td>
                  <td className="p-3 text-[#A8A29E] font-mono text-xs whitespace-nowrap">{m.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ---------- B2B tiers ---------- */}
      <Section tag="For companies" title="B2B packages">
        <div className="grid gap-4 md:grid-cols-3">
          {B2B_TIERS.map((t) => (
            <div key={t.key} data-testid={`pricing-b2b-${t.key}`} className={`tacc-card p-6 relative ${t.featured ? "ring-1 ring-[#F59E0B]/40" : ""}`}>
              {t.featured && <div className="absolute top-4 right-4 text-[#B45309] text-[10px] font-mono uppercase tracking-[0.2em]">Most chosen</div>}
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C] mb-2">{t.who}</div>
              <div className="font-display text-2xl mb-3">{t.name}</div>
              <div className="mb-4 flex items-baseline gap-2">
                {t.checkout ? (
                  <>
                    <PriceInput value={prices.b2b[t.key]} onChange={(v) => setPrice("b2b", t.key, v)} testId={`price-b2b-${t.key}`} big />
                    <span className="font-mono text-xs text-[#78716C]">{t.unit}</span>
                  </>
                ) : (
                  <>
                    <span className="font-display text-4xl text-[#1C1917]">Custom</span>
                    <span className="font-mono text-xs text-[#78716C]">{t.unit}</span>
                  </>
                )}
              </div>
              <ul className="space-y-2">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#44403C]">
                    <Check size={14} className="text-[#B45309] mt-1 shrink-0" strokeWidth={1.5} /> <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- Enterprise calculator ---------- */}
      <Section tag="Enterprise · quote builder" title="Work out a per-seat number">
        <div className="tacc-card p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div>
              <label htmlFor="headcount" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C]">How many employees?</label>
              <input
                id="headcount" type="number" min="1" value={headcount}
                onChange={(e) => setHeadcount(e.target.value)}
                data-testid="enterprise-headcount-input"
                className="mt-2 w-full bg-[#FAF7F2] border border-[#E7DFD5] rounded-lg px-4 py-3 text-2xl font-display text-[#1C1917] focus:border-[#F59E0B] focus:outline-none"
              />
              <div className="mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C]">Your per-seat rates (editable)</div>
              <div className="mt-3 space-y-2">
                {tiers.map((t, i) => {
                  const active = quote && Number(headcount) >= t.min && Number(headcount) <= t.max;
                  return (
                    <div key={t.min} className={`flex items-center justify-between gap-3 py-1.5 px-3 rounded ${active ? "bg-[#F59E0B]/10" : ""}`}>
                      <span className={`text-xs ${active ? "text-[#1C1917]" : "text-[#78716C]"}`}>{seatLabel(t)} employees</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[#A8A29E] text-sm">$</span>
                        <input
                          type="number" min="0" value={t.perSeatYear}
                          onChange={(e) => setRate(i, e.target.value)}
                          data-testid={`enterprise-rate-${i}`}
                          className={`w-20 bg-[#FAF7F2] border rounded px-2 py-1 text-sm text-right focus:outline-none focus:border-[#F59E0B] ${active ? "border-[#F59E0B]/50 text-[#B45309] font-semibold" : "border-[#E7DFD5] text-[#1C1917]"}`}
                        />
                        <span className="text-[#A8A29E] text-xs whitespace-nowrap">/ seat / yr</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-[#FFFDF9] border border-[#F59E0B]/30 rounded-xl p-6 text-center" data-testid="enterprise-quote-result">
              {quote ? (
                <>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C] mb-2">Your quote</div>
                  <div className="font-display text-5xl md:text-6xl text-[#B45309] leading-none">{money(quote.annual)}</div>
                  <div className="text-[#78716C] text-sm mt-2">per year</div>
                  <div className="text-[#1C1917] text-sm mt-4">{quote.headcount.toLocaleString()} seats × {money(quote.perSeatYear)}/seat/year</div>
                  <div className="text-[#A8A29E] text-xs mt-1">≈ {money(Math.round(quote.annual / 12))}/month</div>
                </>
              ) : (
                <div className="text-[#78716C] text-sm py-8">Enter a headcount of 250 or more to build an Enterprise quote.</div>
              )}
            </div>
          </div>
          <p className="text-xs text-[#A8A29E] mt-6">Edit any rate above and hit <span className="text-[#78716C]">Save changes</span> at the top — the quote builder, the one-pager and the proposal emails to leads all use these numbers.</p>
        </div>
      </Section>
    </div>
  );
};
